import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect, ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {HeaderComponent} from "../../common/header/header.component";
import {
  IonButton, IonButtons, IonCol,
  IonContent, IonIcon, IonInfiniteScroll, IonInfiniteScrollContent, IonMenuToggle, IonRow, IonSkeletonText, IonSpinner,
} from "@ionic/angular/standalone";
import {PropertyCardComponent} from "../common/property-card/property-card.component";
import {FooterComponent} from "../../common/footer/footer.component";
import {PropertyService} from "../property.service";
import { FormsModule, ReactiveFormsModule} from "@angular/forms";
import {CurrencyPipe} from "@angular/common";
import {ActivatedRoute} from "@angular/router";
import {GoogleMap, MapMarker} from "@angular/google-maps";
import {FilterService} from "../../common/filter/filter.service";
import {PipesModule} from "../../common/filter/pipes/pipes.module";
import {environment} from "../../../environments/environment";
import {CustomSortPipe} from '../../core/custom-sort.pipe';
import {ListingFilterComponent} from './listing-filter/listing-filter.component';
import {PropertyFilterPipe} from '../../common/filter/pipes/property-filter.pipe';
import {CurrencyConvertService} from '../../core/currency.convert.service';
import {Meta, Title} from '@angular/platform-browser';
import {TranslateModule} from '@ngx-translate/core';
import { CommonModule } from '@angular/common';


@Component({
    selector: 'app-listing-page',
    templateUrl: './listing-page.component.html',
    styleUrls: ['./listing-page.component.scss'],
    standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    IonContent,
    HeaderComponent,
    PropertyCardComponent,
    FooterComponent,
    IonButton,
    ReactiveFormsModule,
    FormsModule,
    GoogleMap,
    PipesModule,
    CustomSortPipe,
    ListingFilterComponent,
    PropertyFilterPipe,
    IonInfiniteScroll,
    IonSpinner,
    MapMarker,
  ],
    providers: [CurrencyPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ListingPageComponent implements OnInit {
  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChild('scrollContainer', { static: true}) private scrollContainer!: ElementRef;
  isScrolled: boolean = false;
  propertyService: PropertyService = inject(PropertyService);
  filterService: FilterService = inject(FilterService);
  route: ActivatedRoute = inject(ActivatedRoute);
  properties: any = [];
  propertiesMap: any = [];
  resultsLength: number = 0;
  subCategoryId: WritableSignal<string> = signal('');
  isLoading: WritableSignal<boolean> = signal(true);

  coordinates: { lat: number, lng: number } = { lng: 46.738586, lat: 24.774265};
  mapOptions: google.maps.MapOptions = {
    mapId: environment.mapId,
    disableDefaultUI: true,
    zoomControl: true,
  };
  currencyPipe: CurrencyPipe = inject(CurrencyPipe);
  param: any;
  title: Title = inject(Title);
  meta: Meta = inject(Meta);
  constructor() {
    effect(async () => {
      if (this.currencyConvertService.localCurrency()) {
        if (!this.map) {
          return;
        }
        const map = this.map.googleMap!;
        if (!map) {
          return;
        }
        await this.handleMapInitialized(map);
      }
    });
  }


  async ngOnInit() {
    this.title.setTitle('Rent Villas, Apartments & More | Property Listings in Riyadh');

    this.meta.addTags([
      {property: 'og:title', content: 'Rent Villas, Apartments & More | Property Listings in Riyadh'},
      {name: 'description', content: 'Browse our extensive selection of properties for rent in Riyadh, including villas, apartments, and compounds. Find your perfect home today!'},
      {property: 'og:description', content: 'Browse our extensive selection of properties for rent in Riyadh, including villas, apartments, and compounds. Find your perfect home today!'},
      {property: 'og:image', content: 'https://satel-resources.nyc3.digitaloceanspaces.com/02bcebe5-a07e-473a-a6bc-054e254d4ce8.jpg'},
      {property: 'og:site_name', content: 'satel.sa'},
      {property: 'og:url', content: `https://satel.sa/`},
      {property: 'og:type', content: 'article'},
      {name: 'twitter:site', content: 'satel.sa'},
      {name: 'twitter:title', content: 'Rent Villas, Apartments & More | Property Listings in Riyadh'},
      {name: 'twitter:description', content: 'Browse our extensive selection of properties for rent in Riyadh, including villas, apartments, and compounds. Find your perfect home today!'},
      {name: 'twitter:card', content: 'summary'},
      {name: 'twitter:image', content: 'https://satel-resources.nyc3.digitaloceanspaces.com/02bcebe5-a07e-473a-a6bc-054e254d4ce8.jpg'}
    ]);
    this.route.queryParams.subscribe(async (param: any) => {
      this.param = param;
      this.filterService.filterData.set(param);
      if (param.sub) {
        this.subCategoryId.set(param.sub);
        const properties = await this.propertyService.getProperties({ subCatId: param.sub, limit: 10, ver: 2 });
        this.propertiesMap = await this.propertyService.getProperties({ subCatId: param.sub, map: true });
        this.properties = properties.data;
        this.resultsLength = properties.totalCount;
      } else {
        const newParam = {...param, limit: 10, ver: 2};
        const properties = await this.propertyService.getProperties({ ...newParam });
        this.propertiesMap = await this.propertyService.getProperties({ ...param, map: true});
        this.properties = properties.data;
        this.resultsLength = properties.totalCount;
      }
      this.isLoading.set(false);
      try {
        const map = this.map.googleMap!;
        if (!map) {
          return;
        }
        await this.handleMapInitialized(map);
      } catch (error) {}
    });
  }
  markers: WritableSignal<any> = signal([]);
  filteredId: string = '';
  currencyConvertService: CurrencyConvertService = inject(CurrencyConvertService);
  filterQuery: string = '';
  async handleMapInitialized(map: google.maps.Map) {
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
    const markers: any = [];
    for (const x of this.propertiesMap) {
      const priceTag = document.createElement('div');
      priceTag.className = 'price-tag';
      priceTag.textContent = this.currencyPipe.transform(x.price, this.currencyConvertService.localCurrency());

      if (!x.lat && !x.lng) {
        return;
      }
      const marker = new AdvancedMarkerElement({
        map,
        position: {lat: x.lat, lng: x.lng },
        content: priceTag,
        title: x._id
      });
      marker.addListener("click", () => {
        const included = this.properties.filter((item: any) => item._id === x._id);
        if (!included.length) {
          this.getMoreProperties('', x._id);
        }
        this.filteredId = x._id;
        markers.forEach((m: any) => {
          const parentElement = m.content.parentElement;
          const parentElement2 = m.content.parentElement.parentElement;
          if (parentElement.classList.contains('clicked')) {
            parentElement.classList.remove('clicked');
            parentElement2.classList.remove('clicked');
            parentElement.style.zIndex = 0;
          }
        });
        const parentElement: any = priceTag.parentElement;
        parentElement.classList.add('clicked');
        setTimeout(() => {
          this.scrollToTop();
        }, 500);
      });
      markers.push(marker);
    }
    this.markers.set(markers);
  }
  onScroll($event: any) {
    this.isScrolled = $event.detail.scrollTop > 50;
  }
  scrollToTop(): void {
    this.scrollContainer.nativeElement.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  async filterValueChanged($event: any) {
    this.filterQuery = $event;
    console.clear();
    console.log($event);
    const properties = await this.propertyService.getProperties({ propertyNumber: $event });
    console.log(properties);
    this.properties = properties;

  }
  allPropertiesLoaded = false;
  isLoadingMore = signal(false);
  loadMoreManually() {
    const lastId = this.properties[this.properties.length - 1]?._id;
    this.getMoreProperties(lastId);
  }

  async getMoreProperties(lastId: string, missingId?: string) {
    let param = this.param;
    this.isLoadingMore.set(true);
    if (lastId) {
      param = {...param, limit: 10, lastId: lastId};
    } else {
      param = {...param, missingId};
    }
    const moreProperties = await this.propertyService.getProperties({ ...param });
    console.log(moreProperties);
    for (const prop of moreProperties) {
      if (lastId) {
        this.properties.push(prop);
      } else {
        this.properties.unshift(prop);
      }
    }
    this.isLoadingMore.set(false);
    if (missingId) {
      setTimeout(() => {
        this.scrollToTop();
      }, 500);
    }
  }
  onIonInfinite($event: any, lastId: string) {
    console.log($event, lastId, this.properties[this.properties.length - 1]._id);
    this.getMoreProperties(lastId);
    setTimeout(() => {
      $event.target.complete();
    }, 500);
  }

  onMarkerClick(property: any) {
    this.filteredId = property._id;
    const element = document.getElementById(property._id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}
